/* =========================================================================
   THE RACK object PDP — the live model + the bar. PORTED from the inline
   <script type="module"> in mondain-html/watering-can.html (identical in its
   eight siblings; parity phase 3, 2026-08-29).

   WHAT CHANGED AT THE SEAM, and only this:
     - 'three' / 'gltf' / 'orbit' / 'room-env' resolve through the theme's
       head importmap (snippets/scripts.liquid) instead of the gold's data-URI
       importmap — the modules themselves are byte-identical.
     - the GLB is the product's own 3D media on cdn.shopify.com (CORS *),
       loaded with GLTFLoader.load, instead of a base64 blob injected as a
       <script> for file://.
     - the render loop SLEEPS while the stage is off-screen or the tab is
       hidden (IntersectionObserver + visibilitychange) and resumes on return.
     - ADD TO CART passes the variant id; the shell's Shopify cart adapter
       (mondain-shell.js) posts it to /cart/add.js and re-renders the bag.
   Everything else — renderer, tone mapping, environment, camera, controls,
   the 1.0 / bounding-diagonal resting scale ("shrink em down to like 60%"),
   OrbitControls owning the wheel over the canvas — is the gold, line for line.

   window.MDN_RACK_VIEWER is a debug hook: { state, meshes, frames, running,
   error, probe() } — probe() renders one frame and counts the drawn pixels.
   ========================================================================= */
import * as THREE from 'three';
import { GLTFLoader } from 'gltf';
import { OrbitControls } from 'orbit';
import { RoomEnvironment } from 'room-env';

const root = document.querySelector('[data-mdn-hw-page]');
const stage = root && root.querySelector('[data-mdn-hwp-stage]');
const canvas = root && root.querySelector('[data-mdn-hwp-gl]');
const loadEl = root && root.querySelector('[data-mdn-hwp-loading]');
const modelUrl = canvas && canvas.getAttribute('data-mdn-hwp-model');

const debug = (window.MDN_RACK_VIEWER = {
  state: 'idle', meshes: 0, frames: 0, running: false, error: null, probe: null
});

if (canvas && modelUrl) {
  /* the object, live — right half of the hero */
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.3;
  const pmrem = new THREE.PMREMGenerator(renderer);
  const scene = new THREE.Scene();
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 1.5;
  scene.add(new THREE.HemisphereLight(0xffffff, 0xd8d4cc, 0.35));
  const camera = new THREE.PerspectiveCamera(34, 1, 0.01, 20);
  camera.position.set(0, 0.34, 2.4);
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.06;
  controls.autoRotate = true;
  controls.autoRotateSpeed = 1.0;
  controls.enablePan = false;
  controls.minDistance = 1.2;
  controls.maxDistance = 4.5;

  function size() {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  addEventListener('resize', size);

  /* the loop, with a sleep: it only runs while the model is in, the stage
     is on screen and the tab is visible; every return path wakes it */
  let loaded = false;
  let onScreen = true;
  let tabVisible = !document.hidden;
  let raf = 0;

  function frame() {
    raf = 0;
    if (!loaded || !onScreen || !tabVisible) { debug.running = false; return; }
    controls.update();
    renderer.render(scene, camera);
    debug.frames++;
    raf = requestAnimationFrame(frame);
  }

  function wake() {
    if (!loaded || !onScreen || !tabVisible || raf) return;
    size();
    debug.running = true;
    raf = requestAnimationFrame(frame);
  }

  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      onScreen = entries.some((e) => e.isIntersecting);
      if (onScreen) wake();
    }, { threshold: 0 }).observe(stage || canvas);
  }
  document.addEventListener('visibilitychange', () => {
    tabVisible = !document.hidden;
    if (tabVisible) wake();
  });

  debug.distance = function () { return controls.getDistance(); };

  debug.probe = function () {
    if (!loaded) return null;
    size();
    renderer.render(scene, camera);
    const gl = renderer.getContext();
    const w = gl.drawingBufferWidth, h = gl.drawingBufferHeight;
    const px = new Uint8Array(w * h * 4);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, px);
    let drawn = 0;
    const colours = new Set();
    for (let i = 0; i < px.length; i += 4) {
      if (px[i + 3] === 0) continue;
      drawn++;
      if (colours.size < 4096) colours.add((px[i] << 16) | (px[i + 1] << 8) | px[i + 2]);
    }
    return { w, h, drawn, distinct: colours.size };
  };

  function fail(err) {
    debug.state = 'error';
    debug.error = err && (err.message || String(err)) || 'load failed';
    if (loadEl) loadEl.textContent = 'Could not load this object';
    if (stage) stage.setAttribute('data-viewer-failed', 'true');
  }

  debug.state = 'loading';
  new GLTFLoader().load(modelUrl, (g) => {
    const box = new THREE.Box3().setFromObject(g.scene);
    const c = box.getCenter(new THREE.Vector3());
    const s = box.getSize(new THREE.Vector3()).length();
    g.scene.position.sub(c);
    const holder = new THREE.Group();
    holder.add(g.scene);
    holder.scale.setScalar(1.0 / s);   /* ~60% of the last cut (Seamas:
      models loaded in way too big) */
    scene.add(holder);
    let meshes = 0;
    g.scene.traverse((o) => { if (o.isMesh) meshes++; });
    debug.meshes = meshes;
    debug.state = 'ready';
    if (loadEl) loadEl.hidden = true;
    if (stage) stage.setAttribute('data-viewer-ready', 'true');
    loaded = true;
    size();
    wake();
  }, undefined, fail);
}

/* the bar: one object, one bag line */
const atc = root && root.querySelector('[data-mdn-hwp-atc]');
if (atc) {
  atc.addEventListener('click', () => {
    if (window.MDN && window.MDN.cart) {
      const variantId = Number(root.getAttribute('data-mdn-hwp-variant') || 0);
      window.MDN.cart.add({
        title: root.getAttribute('data-mdn-hwp-title') || 'Object',
        variant: '1 of 1',
        img: root.getAttribute('data-mdn-hwp-img') || null,
        price: Number(root.getAttribute('data-mdn-hwp-price') || 0),
        ph: 'pr-1',
        variantId: variantId || null,
        qty: 1
      });
    }
  });
}


/* the Shop tooltip is exactly as wide as ATC + the square: measure the
   pair into a custom property the stylesheet reads */
(function () {
  var bar = root.querySelector('[data-mdn-bar], .mdn-pdp__bar');
  var atcEl = root.querySelector('[data-mdn-atc], [data-mdn-hwp-atc]');
  var sq = root.querySelector('[data-mdn-shoppay]');
  if (!bar || !atcEl || !sq) return;
  function fit() { bar.style.setProperty('--pdp-pair-w', (atcEl.offsetWidth + sq.offsetWidth) + 'px'); }
  fit();
  window.addEventListener('resize', fit, { passive: true });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
})();
/* BUY WITH SHOP (the purple square): the cart permalink takes the object
   straight into Shop Pay checkout, no product form needed */
const shoppay = root && root.querySelector('[data-mdn-shoppay], [data-mdn-hwp-shoppay]');
if (shoppay) {
  shoppay.addEventListener('click', () => {
    const variantId = Number(root.getAttribute('data-mdn-hwp-variant') || 0);
    if (!variantId) return;
    window.location.href = '/cart/' + variantId + ':1?payment=shop_pay';
  });
}
